import React, { useState } from 'react';
import { useStore } from '@/context/StoreContext';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Seo } from '@/components/Seo';
import { CONTACT_EMAIL, COMPANY_ADDRESS } from '@/components/layout/Footer';
import { MapPin, Mail, Clock, Loader2 } from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

const field = 'w-full bg-transparent border border-nw-gold/25 rounded-[3px] px-3 h-11 text-nw-ivory placeholder:text-nw-warmgrey/50 focus:outline-none focus:border-nw-gold text-sm';
const label = 'block text-[10px] tracking-luxe uppercase text-nw-warmgrey mb-2';

export default function Contact() {
  const { t, lang, customer } = useStore();
  const [form, setForm] = useState({ name: customer?.full_name || '', email: customer?.email || '', phone: '', inquiry_type: 'purchase', message: '' });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await api.contact({ ...form, source: 'contact_page', lang }); setSent(true); toast.success(t.quote.success); }
    catch { toast.error(t.common.error); }
    finally { setLoading(false); }
  };

  const details = [
    { icon: MapPin, title: t.contact.address, value: COMPANY_ADDRESS },
    { icon: Mail, title: t.contact.email, value: CONTACT_EMAIL, href: `mailto:${CONTACT_EMAIL}`, testId: 'contact-page-email' },
    { icon: Clock, title: t.contact.hours, value: t.contact.hoursVal },
  ];

  return (
    <div className="bg-nw-black">
      <Seo title={t.contact.title} description={lang === 'fr' ? 'Contactez le concierge NICEWINE à Paris : achat, import, export ou recherche de bouteilles rares. Réponse sous un jour ouvré.' : 'Contact the NICEWINE concierge in Paris for purchases, import, export or rare bottle searches. Reply within one business day.'} path="/contact" lang={lang} />
      <div className="nw-container py-16">
        <div className="text-center mb-14">
          <div className="nw-eyebrow mb-3">NICEWINE</div>
          <h1 className="font-serif text-4xl sm:text-5xl text-nw-ivory">{t.contact.title}</h1>
          <p className="mt-3 text-nw-warmgrey text-sm">{t.contact.subtitle}</p>
        </div>
        <div className="grid lg:grid-cols-2 gap-12">
          <div className="space-y-6">
            {details.map((d, i) => (
              <div key={i} className="flex gap-4 items-start border-b border-nw-gold/12 pb-6">
                <d.icon className="text-nw-gold shrink-0 mt-1" size={20} strokeWidth={1.2} />
                <div>
                  <div className="text-[10px] tracking-luxe uppercase text-nw-warmgrey">{d.title}</div>
                  {d.href ? <a data-testid={d.testId} href={d.href} className="text-nw-ivory mt-1 inline-block hover:text-nw-gold transition-colors">{d.value}</a> : <div className="text-nw-ivory mt-1">{d.value}</div>}
                </div>
              </div>
            ))}
          </div>
          <div className="bg-nw-espresso border border-nw-gold/20 p-8">
            {sent ? (
              <div data-testid="contact-success" className="py-12 text-center"><p className="text-nw-ivory">{t.quote.success}</p></div>
            ) : (
              <form onSubmit={submit} className="space-y-4">
                <div><label className={label}>{t.contact.name}</label><input required data-testid="contact-name" className={field} value={form.name} onChange={set('name')} /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><label className={label}>{t.contact.email}</label><input required type="email" data-testid="contact-email" className={field} value={form.email} onChange={set('email')} /></div>
                  <div><label className={label}>{t.contact.phone}</label><input className={field} value={form.phone} onChange={set('phone')} /></div>
                </div>
                <div>
                  <label className={label}>{t.contact.type}</label>
                  <Select value={form.inquiry_type} onValueChange={set('inquiry_type')}>
                    <SelectTrigger data-testid="contact-type-select" className={field}><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-nw-espresso border-nw-gold/25 text-nw-ivory">
                      {Object.entries(t.quote.types).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><label className={label}>{t.contact.message}</label><textarea required data-testid="contact-message" rows={5} className={field + ' h-auto py-2'} value={form.message} onChange={set('message')} /></div>
                <button type="submit" disabled={loading} data-testid="contact-submit-button" className="nw-btn-gold w-full">{loading ? <Loader2 size={16} className="animate-spin" /> : t.contact.send}</button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
